/**
 * Types for the Storefront Builder
 * Aligned with backend FunnelNode and FunnelEdge structure
 */

import { TrackingFormValues } from "@/app/stores/[id]/storefront/types/tracking";

/**
 * Valid step types for funnel steps
 * MUST match backend schema: src/lib/schemas/funnelv2.ts
 * Source of truth: FunnelNode.type
 * NEVER add other types without updating ALL places!
 * 
 * NOTE: We use 'offer' for all offer types (no separate upsell/downsell)
 */
export type StepType =
    | 'landing'
    | 'checkout'
    | 'thankyou'
    | 'offer'
    | 'error'
    | 'custom'
    | 'external'
    | 'storefront_shopify'
    | 'storefront_woocommerce';

/**
 * =============================================================================
 * FUNNEL V2 TYPES (Local copy from backend)
 * =============================================================================
 */

/**
 * Funnel Node (Step in the flow)
 */
export interface FunnelNode {
    id: string;
    name: string;
    kind?: 'step' | 'action' | 'condition' | 'integration';
    type: StepType;

    // Entry & Conversion Markers
    isDefault?: boolean;
    isConversion?: boolean;

    // Visual Position
    position?: {
        x: number;
        y: number;
    };

    // Legacy positioning (for backward compatibility)
    index?: number;
    row?: number;

    // Configuration
    config?: StepConfig;
    variants?: StepVariant[];
    splitType?: 'weighted' | 'geo';

    // Metadata
    metadata?: Record<string, any>;
}

/**
 * Funnel Edge (Connection between nodes)
 */
export interface FunnelEdge {
    id: string;
    source: string;
    target: string;

    // Handle connections (for bidirectional flow)
    sourceHandle?: string;
    targetHandle?: string;

    // Display
    label?: string;

    // Routing Logic
    conditions?: {
        when?: ConditionExpression;
        priority?: number;
        bypassTo?: string;
    };

    // Metadata
    metadata?: Record<string, any>;
}

/**
 * Funnel Configuration V2
 */
export interface FunnelConfigV2 {
    id?: string;
    name: string;
    description?: string;
    version: string;

    // Core: Nodes + Edges
    nodes: FunnelNode[];
    edges: FunnelEdge[];

    // Backward compatibility
    steps?: any[];

    routing?: {
        pluginId?: string;
        basePath?: string;
        deployments?: Record<string, any>;
    };
    metadata?: Record<string, any>;
}

export type ABTestType = 'config' | 'path';

// Type of alternative/split routing
export type AlternativeType =
    | 'weighted'      // Random weighted A/B test (50/50, 60/40, etc.)
    | 'conditional'   // Based on conditions (if/else logic)
    | 'sequential';   // Sequential rotation

export type ConditionOperator = 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than' | 'exists';

export interface ConditionRule {
    field: string;
    operator: ConditionOperator;
    value: string | number | boolean;
    description?: string;
}

/**
 * Condition Expression (JSONLogic + Simple String)
 * 
 * Supports three formats:
 * 1. Simple string: "payment.success"
 * 2. JSONLogic: { "==": [{ "var": "payment.status" }, "success"] }
 * 3. Custom operators: { "cart.hasItem": { "productId": "123" } }
 * 
 * JSONLogic Examples:
 * - Equal: { "==": [{ "var": "payment.status" }, "success"] }
 * - Greater than: { ">": [{ "var": "cart.total" }, 100] }
 * - AND: { "and": [condition1, condition2] }
 * - OR: { "or": [condition1, condition2] }
 * - NOT: { "!": condition }
 * - IN: { "in": ["pro", { "var": "customer.tags" }] }
 */
export type ConditionExpression =
    | string                                    // Simple: "payment.success"
    | Record<string, any>;                      // JSONLogic or custom operators

export type EntryPlatform = 'shopify' | 'woocommerce' | 'custom' | 'direct' | 'other';
export type EntryChannel = 'organic' | 'paid' | 'email' | 'sms' | 'social' | 'direct' | 'affiliate' | 'other';

export interface EntrySource {
    platform?: EntryPlatform;
    channel?: EntryChannel;
    campaign?: string;
    description?: string;
    // Optional: Match conditions for routing (e.g., UTM params, referrer)
    matchWhen?: Record<string, any>;
}

export interface StepConditions {
    entry?: boolean; // Is this an entry point?
    entrySource?: EntrySource; // Entry source configuration (only relevant if entry = true)
    isDefault?: boolean; // Is this the default path (fallback for conditionals)?
    from?: string[]; // Source step IDs
    when?: ConditionExpression | ConditionRule[]; // Conditional logic - determines ROW (different paths)
    priority?: number; // Priority order (higher = evaluated first)
    bypassTo?: string; // Step ID to bypass to if condition fails
    timeout?: number; // Timeout in seconds before auto-advance
    percentage?: number; // For A/B testing - splits traffic WITHIN same row (config variants)
}

/**
 * Script injection configuration
 */
export interface ScriptConfig {
    name: string;               // Human-readable name (e.g., "Facebook Pixel", "Custom Analytics")
    enabled: boolean;           // Whether this script is active
    content: string;            // The actual script code
    position?: 'head-start' | 'head-end' | 'body-start' | 'body-end';  // Where to inject (default: head-end)
}

/**
 * Runtime Step Configuration
 * 
 * Settings that CAN BE DIFFERENT per split variant:
 * - Payment flow (different processors per variant)
 * - Scripts (different tracking per variant)
 * - Static resources (different offers per variant)
 * - Pixels (different tracking pixels per variant)
 * 
 * IMPORTANT: For split steps, each variant has its own stepConfig!
 * - Standard step: node.config.stepConfig
 * - Split step: node.variants[i].config.stepConfig
 */
export interface ExpressMethodState {
    enabled: boolean;
}

export interface PaymentMethodEntry {
    enabled: boolean;
    type: 'card' | 'apm' | 'integration';
    label: string;
    logoUrl?: string;
    description?: string;
    /** Free-text prompt shown in the payment selector when this method is
     *  selected (redirect-style methods: paypal, crypto, …). Zelle and
     *  custom_payment store their multi-line copy under settings
     *  (instructionsMessage / checkoutInstructions) instead. */
    promptText?: string;
    /** Card-form field labels — only meaningful on the card entry. They
     *  drive the inline card form rendered by the Studio payment selector
     *  (and are used as the card-number input's placeholder). Undefined
     *  falls through to the Studio node's own design array and then to the
     *  built-in English defaults. */
    cardNumberLabel?: string;
    expiryLabel?: string;
    cvcLabel?: string;
    processorId?: string;
    integrationId?: string;
    provider?: string;
    supportedCountries?: string[];
    presentmentCurrencies?: string[];
    recurring?: boolean;
    /** Step-level express placement override for the native wallets that can
     *  render in the checkout's express section — paypal, apple_pay,
     *  google_pay (never their APM/ECE variants, which are Elements-bound).
     *  Tri-state: true/false wins over the integration's
     *  metadata.enableExpressCheckout (snapshotted into `metadata` at
     *  step-save time); undefined falls through to it, and then to a
     *  per-provider default.
     *
     *  That last fallback is NOT uniform, and the asymmetry is load-bearing:
     *  PayPal defaults to false (express placement shipped opt-in), while
     *  Apple/Google Pay default to true — they have always rendered express
     *  when enabled, and integrations predating the flag carry `undefined`.
     *  See EXPRESS_CAPABLE_PROVIDERS in PaymentSetupConfigEditor and
     *  resolveExpressPlacement in the studio ExpressPaymentWidget. */
    express?: boolean;
    /** Present only on express_checkout:proc_id entries */
    publishableKey?: string;
    /** Present only on express_checkout:proc_id entries — map of apmKey → state */
    methods?: Record<string, ExpressMethodState>;
    /** Present only on crypto integration entries — map of providerId → provider state */
    providers?: Record<string, { enabled: boolean; label?: string; min?: number; note?: string }>;
    metadata?: Record<string, unknown>;
    /** Integration settings (e.g. custom_payment checkout instructions, thank-you text) */
    settings?: Record<string, unknown>;
    /** Display order in checkout (lower = first). Undefined means unordered (uses default priority). */
    order?: number;
}

export interface RuntimeStepConfig {
    // Payment configuration (checkout steps only)
    payment?: {
        paymentFlowId?: string;    // Override payment flow (if not set, uses store default)
    };
    paymentSetupConfig?: Record<string, PaymentMethodEntry>;
    // Per-step payment initiator for upsell/offer steps. Controls whether the
    // post-checkout charge is sent as MIT (merchant-initiated, off-session —
    // no 3DS challenge) or CIT (customer-initiated, may trigger 3DS).
    // Default = 'merchant' (MIT).
    paymentInitiator?: 'merchant' | 'customer';
    // Order bumps configuration
    orderBumps?: {
        mode: 'inherit' | 'custom';
        enabledOfferIds?: string[];
    };
    // Upsell offers configuration (post-checkout offer pages)
    upsellOffers?: {
        mode: 'inherit' | 'custom';
        enabledUpsellIds?: string[];
    };
    // Checkout offer selector (in-step tiered offer picker that swaps cart items).
    // Backed by the `offers` table; each id refers to a `offers.id` row.
    // `enabledOfferIds` is ordered — the chunk renders packs in this order.
    checkoutOffers?: {
        mode: 'inherit' | 'custom';
        enabledOfferIds?: string[];
        defaultOfferId?: string;
        // Per-offer presentation overrides keyed by offerId. The renderer
        // reads these for the currently-active offer (see Zod schema in
        // src/app/useCases/funnels/schemas.ts for the source of truth).
        offerSettings?: Record<string, { description?: string; comparePrice?: number }>;
    };
    // Typed resource bindings (replaces staticResources)
    resources?: Record<string, string>;
    // Static resources (offers, products bound to this step/variant)
    staticResources?: Record<string, string>;
    // Scripts injection (array of named scripts, can be different per variant)
    scripts?: ScriptConfig[];
    // Pixel tracking (different pixels per variant for proper A/B attribution)
    pixels?: Record<string, TrackingFormValues[]>;
    // Per-step shipping rate hide-list. Listed IDs are hidden; new rates auto-show.
    shippingRateSettings?: {
        hiddenShippingRateIds?: string[];
    };
    // Per-step address form config (checkout step).
    addressSettings?: {
        // ISO-3166-1 alpha-2 codes. Empty/missing = all countries allowed in dropdown.
        countryAllowlist?: string[];
        // ISO-3166-1 alpha-2. Applied on initial load only, overrides geolocation.
        forcedCountry?: string;
    };
    // Per-step access restrictions (checkout step). Visitors whose geo-IP
    // country is listed are served a 403 block page at the edge instead of
    // the funnel HTML (see Zod schema in src/app/useCases/funnels/schemas.ts).
    accessSettings?: {
        // ISO-3166-1 alpha-2 codes. Empty/missing = no geo blocking.
        blockedCountries?: string[];
    };
    // Per-step payment translations — Studio-builder-only. Per-method
    // overrides keyed by method type (apple-pay, credit-card, zelle, etc.)
    // and component chrome strings (title, dividerText, errors, card-form
    // labels), keyed by BCP-47 locale.
    paymentSettings?: {
        methods?: Record<string, {
            translations?: Record<string, {
                label?: string;
                description?: string;
                promptText?: string;
                instructions?: string;
            }>;
        }>;
        translations?: Record<string, {
            title?: string;
            description?: string;
            dividerText?: string;
            redirectText?: string;
            processingLabel?: string;
            cardNumberLabel?: string;
            expiryLabel?: string;
            cvcLabel?: string;
            cardNumberError?: string;
            expiryError?: string;
            expiryFutureError?: string;
            cvcError?: string;
        }>;
    };
}

/**
 * Step Config (aligned with backend StepConfig)
 */
export interface StepConfig {
    // Old format (v1)
    path?: string;
    // New format (v2)
    url?: string; // Optional - backend computes from mountPointId + path
    mountPointId?: string;
    entryPointId?: string;
    mountPoint?: string;
    pagePath?: string;
    // Plugin config
    pluginId?: string;
    instanceId?: string;
    instanceVersion?: string;
    routeId?: string;
    // Common fields
    template?: string;
    variant?: string;
    metadata?: {
        routeType?: 'mount-point' | 'external' | 'instance';
        [key: string]: any;
    };
    // Runtime step configuration (payment, scripts, pixels)
    stepConfig?: RuntimeStepConfig;
    // @deprecated - Use stepConfig.payment.paymentFlowId instead
    paymentFlowId?: string; // Legacy: Override payment flow for this step
}

/**
 * Config variant for A/B testing (aligned with backend StepVariant)
 */
export interface StepVariant {
    id: string;
    label: string;
    percentage: number; // For weighted splits
    regions?: string[]; // For geo splits (e.g., ['US', 'CA', 'GB'])
    config: StepConfig;
    metrics?: {
        visitors?: number;
        conversions?: number;
        progressionRate?: number | null;
        conversionValue?: number;
        conversionValueUnit?: string;
        avgConversionValue?: number;
        hasNextStep?: boolean;
    };
}

/**
 * FlowStep: Legacy type for backward compatibility
 * 
 * @deprecated Use FunnelNode instead
 * This type is maintained for components that haven't been migrated yet
 */
export interface FlowStep {
    id: string;
    label: string; // Maps to FunnelNode.name
    type: StepType;
    position: {
        row: number; // Legacy: Conditional path
        col: number; // Legacy: Position in flow
    };

    // ✅ Flow Editor position - actual x,y coordinates
    flowPosition?: {
        x: number;
        y: number;
    };

    // Start page: where a session begins when nothing names a step (#TAG-2775)
    isDefault?: boolean;

    // Conversion goal flag
    isConversion?: boolean;

    // Standard step (no A/B test)
    config?: StepConfig;

    // Split step (A/B test)
    variants?: StepVariant[];
    splitType?: 'weighted' | 'geo';

    conditions: StepConditions;
    connections?: string[]; // IDs of next steps
    alternativeType?: AlternativeType;

    // Node metadata (screenshots, custom data)
    metadata?: Record<string, any>;

    // Aggregated metrics
    metrics?: {
        visitors?: number;
        conversions?: number;
        progressionRate?: number | null;
        conversionValue?: number;
        conversionValueUnit?: string;
        avgConversionValue?: number;
        hasNextStep?: boolean;
    };
}

export interface Storefront {
    id: string;
    name: string;
    slug: string; // Unique URL-friendly identifier (e.g., "main-checkout-flow")
    mountPointType?: 'alias' | 'custom-domain'; // Mount point type: alias (funnel-slug) or custom domain
    abTestType: ABTestType | null;

    // 🎯 New: Nodes + Edges model
    nodes?: FunnelNode[];  // All nodes (steps) in the funnel
    edges?: FunnelEdge[];  // All connections between nodes

    // 🔄 Legacy: Steps model (always defined for backward compatibility)
    steps: FlowStep[];     // Always defined (converted from nodes+edges if needed)

    // Production Deployment Tracking
    productionConfig?: any; // Production configuration (copy of config when promoted)
    productionDomains?: string[]; // List of domains this funnel is live on
    lastPromotedAt?: string | Date; // Timestamp of last promotion
    updatedAt?: string | Date; // Timestamp of last update
}

/**
 * =============================================================================
 * CONVERSION HELPERS
 * =============================================================================
 */

/**
 * Convert FunnelNode to FlowStep (for backward compatibility)
 */
export function nodeToFlowStep(node: FunnelNode, incomingEdges: FunnelEdge[] = []): FlowStep {
    const from = incomingEdges.map(e => e.source);
    const firstEdge = incomingEdges[0];

    return {
        id: node.id,
        label: node.name,
        type: node.type as StepType,
        position: {
            col: node.index ?? 0,
            row: node.row ?? 0,
        },
        flowPosition: node.position,
        isConversion: node.isConversion,
        config: node.config,
        variants: node.variants,
        splitType: node.splitType,
        metadata: node.metadata, // ✅ Include metadata (for screenshots, etc.)
        conditions: {
            entry: node.isDefault,
            from: from.length > 0 ? from : undefined,
            when: firstEdge?.conditions?.when,
            priority: firstEdge?.conditions?.priority,
            bypassTo: firstEdge?.conditions?.bypassTo,
        },
    };
}

/**
 * Convert FlowStep to FunnelNode + edges
 */
export function flowStepToNode(step: FlowStep): {
    node: FunnelNode;
    edges: FunnelEdge[];
} {
    const node: FunnelNode = {
        id: step.id,
        name: step.label,
        kind: 'step',
        type: step.type,
        // The start page: root `isDefault`, else the editor's `conditions.entry` twin (#TAG-2775).
        isDefault: step.isDefault ?? step.conditions.entry ?? step.conditions.isDefault ?? false,
        isConversion: step.isConversion,
        position: step.flowPosition,
        index: step.position.col,
        row: step.position.row,
        config: step.config,
        variants: step.variants,
        splitType: step.splitType,
        metadata: step.metadata,
    };

    const edges: FunnelEdge[] = [];
    if (step.conditions.from) {
        for (const sourceId of step.conditions.from) {
            edges.push({
                id: `edge_${sourceId}_${step.id}`,
                source: sourceId,
                target: step.id,
                conditions: {
                    when: step.conditions.when,
                    priority: step.conditions.priority,
                    bypassTo: step.conditions.bypassTo,
                },
            });
        }
    }

    return { node, edges };
}
